import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    YES, EDIT, IDLE, PENDING, IN_PROGRESS, SUPPOSEDLY_COMPLETED, COMPLETED, DEFERRED,
} from "const"
import {
    TaskStatusType,
    StateType,
    deepCopy,
    replaceHistoryState,
} from "client/utils"
import {
    TaskObjectProps,
    StoreType,
    TaskList,

    LoadTaskById,
    GetTaskAccess,
    UpdateTaskStatus,
    DeleteTask,
    setTaskList,
    addModal,
    moduleIds,
} from "client/store"
import {
    CKEditorAttachmentsView,
    FormGroup,
    Label,
    Input,
    DivInput,
    Empty,
    Button,
    Delimiter,
    SpinnerIcon,
    PlusIcon,
    DelegateIcon,
    PauseIcon,
    ResumeIcon,
    CrossIcon,

    PopupMenu,
    PopupMenuItem,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmBottom,
} from "client/components"

import TaskEdit from "client/pages/task/edit"
import DelegateUsers from "./delegateUsers"

import {
    TaskForm,
    PopupSpan,
} from "./styled"

// TODO: check conditions from list popup menu
const Form = ({
    task,
}: TaskObjectProps) => {
    const baseRoute = useSelector((state: StoreType) => state.task.baseRoute)
    const taskList = useSelector((state: StoreType) => state.task.taskList)
    const taskParticipantList = useSelector((state: StoreType) => state.taskParticipant.taskParticipantList)
    const taskObserverList = useSelector((state: StoreType) => state.taskObserver.taskObserverList)
    const attachmentsView = useSelector((state: StoreType) => state.task.attachmentsView)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const {
        subTaskModuleAccess,
        lifecycleModuleAccess,
    } = packageSettings

    const [showPopup, setShowPopup] = useState(false)
    const [popupTarget, setPopupTarget] = useState<HTMLElement>(null)

    const [showConfirm, setShowConfirm] = useState(false)
    const [deleteState, setDeleteState] = useState<StateType>(IDLE)

    const hideMenu = () => {
        setShowPopup(false)
    }

    useEffect(() => {
        document.addEventListener("click", hideMenu)
        return () => document.removeEventListener("click", hideMenu)
    }, [])

    const updateStatus = (status: TaskStatusType) => {
        UpdateTaskStatus(task.id, status).then(() => {
            LoadTaskById(task.id, userData.accountId).then((res) => {
                const taskListCopy: TaskList = taskList ? deepCopy(taskList) : {}
                taskListCopy[task.id] = res
                dispatch(setTaskList(taskListCopy))
            })
        })
    }

    const onCancel = () => {
        setShowConfirm(false)
    }

    const onConfirm = () => {
        setDeleteState(PENDING)
        DeleteTask(task.id, userData.accountId).then((removedRows) => {
            setDeleteState(IDLE)
            if (removedRows === 1) {
                const taskListCopy: TaskList = taskList ? deepCopy(taskList) : {}
                delete taskListCopy[task.id]
                dispatch(setTaskList(taskListCopy))
            }
        })
    }

    const attachments = attachmentsView[task.id] || []
    const parentTask = task.taskId ? taskList[task.taskId] : null

    let lifecycleModuleEnabled = false
    if (task.project) {
        const projectModules = JSON.parse(task.project.modules)
        lifecycleModuleEnabled = !!projectModules[moduleIds.lifecycle]
    }

    const editUrl = `${baseRoute}/${EDIT}/${task.id}`
    const addUrl = `${baseRoute}/${EDIT}/0`

    const {
        isCreator,
        isResponsible,
        isParticipant,

        addAccess,
        allowedToEdit,
        allowedToDelete,
    } = GetTaskAccess(task, taskParticipantList, taskObserverList, userData)

    return (
        <TaskForm>
            <Row>
                <Col xs={12}>
                    <CKEditorAttachmentsView
                        description={task.description}
                        attachments={attachments}
                    />
                    <Delimiter size="sm" />

                    <FormGroup>
                        <Label>{lang.project}</Label>
                        {
                            task.project
                            && <Input type="text" value={`${task.project.name} (${task.project.publicId})`} readOnly />
                        }
                        {
                            !task.project
                            && <DivInput widePadding><Empty /></DivInput>
                        }
                    </FormGroup>

                    {
                        lifecycleModuleAccess === YES
                        && lifecycleModuleEnabled
                        && (
                            <FormGroup>
                                <Label>{lang.lifecyclePhase}</Label>
                                {
                                    task.phase
                                    && <Input type="text" value={`${task.phase.lifecycle.name} > ${task.phase.name}`} readOnly />
                                }
                                {
                                    !task.phase
                                    && <DivInput widePadding><Empty /></DivInput>
                                }
                            </FormGroup>


                        )
                    }

                    {
                        subTaskModuleAccess === YES && (
                            <FormGroup>
                                <Label>{lang.parentTask}</Label>
                                {
                                    parentTask
                                    && <Input type="text" value={parentTask.name} readOnly />
                                }
                                {
                                    !parentTask
                                    && <DivInput widePadding><Empty /></DivInput>
                                }
                            </FormGroup>
                        )
                    }

                    <Delimiter size="md" />
                </Col>


                <Col xs={12}>
                    {
                        (isResponsible || isParticipant || allowedToEdit)
                        && task.status === PENDING
                        && (
                            <Button variant="success" row onClick={() => updateStatus(IN_PROGRESS)}>
                                {lang.start}
                            </Button>
                        )
                    }
                    {
                        (isResponsible || isParticipant || allowedToEdit)
                        && task.status === IN_PROGRESS
                        && (
                            <Button variant="success" row onClick={() => updateStatus(PENDING)}>
                                {lang.pause}
                            </Button>
                        )
                    }
                    {
                        (isCreator || allowedToEdit)
                        && task.status === SUPPOSEDLY_COMPLETED
                        && (
                            <Button variant="danger" row onClick={() => updateStatus(IN_PROGRESS)}>
                                {lang.returnForRevision}
                            </Button>
                        )
                    }
                    {
                        (isResponsible || isParticipant || isCreator || allowedToEdit)
                        && task.status !== COMPLETED // includes: PENDING, IN_PROGRESS, DEFERRED, SUPPOSEDLY_COMPLETED
                        && (
                            <Button
                                variant="success"
                                row
                                onClick={() => {
                                    if (isCreator) updateStatus(COMPLETED)
                                    else if (isResponsible || isParticipant || allowedToEdit) updateStatus(SUPPOSEDLY_COMPLETED)
                                }}
                            >
                                {lang.finish}
                            </Button>
                        )
                    }

                    <Button
                        row
                        after
                        onClick={(e) => {
                            e.stopPropagation()
                            setShowPopup(true)
                            setPopupTarget(e.currentTarget as HTMLElement)
                        }}
                    >
                        {lang.more}
                    </Button>

                    {
                        allowedToEdit && (
                            <Button onClick={() => dispatch(addModal({ url: editUrl }))}>
                                {lang.edit}
                            </Button>
                        )
                    }
                </Col>
            </Row>

            <PopupMenu
                show={showPopup}
                target={popupTarget}
                placement="bottom-start"
                showArrow={false}
            >
                {
                    addAccess && subTaskModuleAccess === YES && (
                        <PopupMenuItem onClick={() => {
                            const prevUrl = window.location.pathname
                            replaceHistoryState(addUrl)
                            dispatch(addModal({
                                key: "addSubtask",
                                content: <TaskEdit taskId={0} parentTaskId={task.id} />,
                                onClose: () => replaceHistoryState(prevUrl),
                            }))
                        }}
                        >
                            <PopupSpan>
                                <PlusIcon />
                                {lang.createSubtask}
                            </PopupSpan>
                        </PopupMenuItem>
                    )
                }
                {
                    (isResponsible || isParticipant || isCreator || allowedToEdit) && (
                        <PopupMenuItem onClick={() => dispatch(addModal({
                            key: "delegate",
                            content: <DelegateUsers title={lang.delegateTo} task={task} />,
                        }))}
                        >
                            <PopupSpan>
                                <DelegateIcon />
                                {lang.delegate}
                            </PopupSpan>
                        </PopupMenuItem>
                    )
                }
                {
                    (isResponsible || isParticipant || isCreator || allowedToEdit)
                    && [PENDING, IN_PROGRESS].includes(task.status)
                    && (
                        <PopupMenuItem onClick={() => updateStatus(DEFERRED)}>
                            <PopupSpan>
                                <PauseIcon />
                                {lang.defer}
                            </PopupSpan>
                        </PopupMenuItem>
                    )
                }
                {
                    (isResponsible || isParticipant || isCreator || allowedToEdit)
                    && [DEFERRED, SUPPOSEDLY_COMPLETED, COMPLETED].includes(task.status)
                    && (
                        <PopupMenuItem onClick={() => updateStatus(PENDING)}>
                            <PopupSpan>
                                <ResumeIcon />
                                {lang.resume}
                            </PopupSpan>
                        </PopupMenuItem>
                    )
                }
                {
                    allowedToDelete && (
                        <PopupMenuItem onClick={() => setShowConfirm(true)}>
                            <PopupSpan>
                                <CrossIcon />
                                {lang.delete}
                            </PopupSpan>
                        </PopupMenuItem>
                    )
                }
            </PopupMenu>


            <ConfirmWrapper show={showConfirm} onClose={onCancel}>
                <Confirm>
                    <ConfirmTitle>{lang.deleteTaskConfirm}</ConfirmTitle>
                    <ConfirmBottom>
                        <Button variant="success" row onClick={onConfirm}>
                            {deleteState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                        <Button row onClick={onCancel}>{lang.cancel}</Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </TaskForm>
    )
}
export default Form
